/**
 * ChatMetricsStrip · v2.7.65+E108 (+E109 queue-overflow banner)
 * ══════════════════════════════════════════════════════════════════
 * Global "Active Chats · Queue Length · Resolved Today" strip that
 * sits above the admin dashboard widget grid. Polls the new
 * /api/admin/chat/metrics endpoint every 8 s. Zero-config — every
 * admin sees the same numbers, live.
 *
 * v2.7.65+E109 additions:
 *   • When queue_length exceeds `overflow_threshold` (default 5), a
 *     floating dashboard banner slides down with a "STAFF UP" CTA and
 *     the direct chat audio alert fires so every online agent hears
 *     the ping.
 *   • Idempotent audio: only fires when the threshold flips false→true
 *     within this component's lifetime, so a persistent overflow does
 *     not turn into a constant siren.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Clock, CheckCircle2, AlertTriangle, Siren, X, BellOff } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";

export default function ChatMetricsStrip() {
  const [m, setM] = useState({
    active_chats: 0, queue_length: 0, resolved_today: 0, auto_filed: 0,
    overflow: false, overflow_threshold: 5,
  });
  const [loaded, setLoaded] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [muting, setMuting] = useState(false);
  const wasOverflow = useRef(false);

  // v2.7.65+E111 · Cooldown toggle — mutes the SMS escalation for the
  // current shift while keeping the audio + banner alive so agents still
  // see the surge.
  const muteEscalation = async () => {
    setMuting(true);
    try {
      await adminApi.post("/admin/chat/overflow-cooldown", { duration_min: 60 });
      toast.success("SMS escalation muted for 60 min");
      // Optimistically hide the banner; next tick will reflect state.
      setBannerDismissed(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cooldown failed");
    } finally { setMuting(false); }
  };
  const unmuteEscalation = async () => {
    setMuting(true);
    try {
      await adminApi.delete("/admin/chat/overflow-cooldown");
      toast.success("SMS escalation resumed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unmute failed");
    } finally { setMuting(false); }
  };

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await adminApi.get("/admin/chat/metrics");
        if (!cancelled && data) {
          setM(data);
          // Rising-edge audio + reset dismissal so agents can't
          // permanently mute a fresh overflow event.
          if (data.overflow && !wasOverflow.current) {
            try {
              window.dispatchEvent(new CustomEvent("rtg:chat_message_direct", {
                detail: { reason: "queue_overflow", queue_length: data.queue_length },
              }));
            } catch { /* noop */ }
            setBannerDismissed(false);
          }
          wasOverflow.current = !!data.overflow;
        }
      } catch { /* silent — no strip when unauthenticated */ }
      finally { if (!cancelled) setLoaded(true); }
    };
    tick();
    const t = setInterval(tick, 8000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const queueHot = (m.queue_length || 0) > 0;
  const autoHot  = (m.auto_filed  || 0) > 0;

  return (
    <>
      {/* v2.7.65+E109 · Floating queue-overflow banner. Slides down when
          the queue exceeds the threshold; dismissible per session. */}
      {m.overflow && !bannerDismissed && (
        <div
          data-testid="admin-queue-overflow-banner"
          className="mb-3 rounded-lg border-2 border-rose-400 bg-gradient-to-r from-rose-600 to-amber-600 text-white px-4 py-3 shadow-lg flex items-center gap-3 animate-pulse"
        >
          <Siren className="h-5 w-5 flex-shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="font-black text-sm uppercase tracking-wider" data-testid="queue-overflow-title">
              Queue Overflow · {m.queue_length} riders waiting
            </div>
            <div className="text-[11px] opacity-90">
              Threshold is {m.overflow_threshold}. Every online agent — please pick up a chat in Live Chat.
            </div>
          </div>
          <Link
            to="/admin/chat"
            data-testid="queue-overflow-cta"
            className="bg-white text-rose-700 hover:bg-rose-50 font-bold text-xs px-3 py-1.5 rounded uppercase tracking-widest whitespace-nowrap"
          >
            Staff Up
          </Link>
          <button
            type="button"
            onClick={muteEscalation}
            disabled={muting}
            data-testid="queue-overflow-mute"
            className="bg-white/20 hover:bg-white/30 text-white font-bold text-[11px] px-2.5 py-1.5 rounded uppercase tracking-widest whitespace-nowrap flex items-center gap-1"
            title="Mute SMS escalation for 60 min (banner + audio stay on)"
          >
            <BellOff className="h-3 w-3"/>Mute SMS
          </button>
          <button
            type="button"
            onClick={() => setBannerDismissed(true)}
            data-testid="queue-overflow-dismiss"
            className="text-white/70 hover:text-white p-1 rounded"
            title="Hide until next overflow spike"
          >
            <X className="h-3.5 w-3.5"/>
          </button>
        </div>
      )}

      {/* v2.7.65+E111 · Cooldown active badge — sits above the strip when
          the SMS escalation is muted so ops always sees the mute state. */}
      {m.cooldown?.active && (
        <div
          data-testid="admin-cooldown-badge"
          className="mb-3 rounded-md bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-800 px-3 py-2 flex items-center gap-2"
        >
          <BellOff className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300"/>
          <div className="flex-1 min-w-0 text-xs">
            <div className="font-bold text-amber-800 dark:text-amber-200">
              SMS escalation muted
            </div>
            <div className="text-[11px] text-amber-700 dark:text-amber-300">
              Set by {m.cooldown.set_by || "supervisor"} · resumes at{" "}
              {m.cooldown.expires_at ? new Date(m.cooldown.expires_at).toLocaleTimeString() : "—"}
              {m.cooldown.reason ? ` · ${m.cooldown.reason}` : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={unmuteEscalation}
            disabled={muting}
            data-testid="admin-cooldown-unmute"
            className="text-[11px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-white dark:bg-zinc-900 border border-amber-400 text-amber-800 dark:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-900/50"
          >
            Resume
          </button>
        </div>
      )}

      <div
        data-testid="admin-chat-metrics-strip"
        className="mb-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-white"
      >
        <MetricPill
          label="Active Chats"
          value={m.active_chats}
          Icon={MessageSquare}
          color="from-blue-600 to-blue-500"
          testid="chat-metric-active"
        />
        <MetricPill
          label="Queue Length"
          value={m.queue_length}
          Icon={Clock}
          color={m.overflow
            ? "from-rose-600 to-rose-500 animate-pulse"
            : queueHot ? "from-amber-500 to-rose-500 animate-pulse"
                       : "from-zinc-700 to-zinc-600"}
          testid="chat-metric-queue"
        />
        <MetricPill
          label="Resolved Today"
          value={m.resolved_today}
          Icon={CheckCircle2}
          color="from-emerald-600 to-emerald-500"
          testid="chat-metric-resolved"
        />
        <MetricPill
          label="Auto-Ticketed (SLA)"
          value={m.auto_filed}
          Icon={AlertTriangle}
          color={autoHot ? "from-rose-600 to-rose-500"
                         : "from-zinc-800 to-zinc-700"}
          testid="chat-metric-autofiled"
          subtle={!loaded}
        />
      </div>
    </>
  );
}

function MetricPill({ label, value, Icon, color, testid, subtle }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-lg px-3 py-2 bg-gradient-to-br ${color} shadow-inner ${subtle ? "opacity-70" : ""}`}
    >
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-[10px] uppercase tracking-widest font-bold opacity-80">
          {label}
        </span>
        <Icon className="h-3.5 w-3.5 opacity-80"/>
      </div>
      <div className="text-2xl font-black leading-none">
        {value ?? 0}
      </div>
    </div>
  );
}
